import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

/**
 * Shared page shell: header wordmark + a footer with the legal links.
 *
 * Type follows the app's two-tier split (docs/DESIGN-SYSTEM.md §9.4): the
 * wordmark and page title are Baloo 2, everything anyone actually reads is
 * Nunito. Nav labels are lowercase, matching the app's UI voice; prose and legal
 * headings keep normal sentence case, because a privacy policy is a document,
 * not chrome.
 */
export function PageChrome({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="border-b-[2.5px] border-ink bg-surface">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between px-6 py-4">
          <Link
            to="/"
            className="font-display text-xl font-bold tracking-tight text-ink"
          >
            Bible Shield
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-4xl flex-1 px-6 py-12">
        {title ? (
          <h1 className="font-display text-[34px] leading-[1.3] font-bold tracking-tight text-ink">
            {title}
          </h1>
        ) : null}
        {children}
      </main>

      <footer className="border-t-[2.5px] border-ink bg-surface">
        <div className="mx-auto flex w-full max-w-4xl flex-wrap gap-5 px-6 py-8 text-sm text-ink2">
          {/* Same reason as the landing page's footer: every page links to the
              blog, so no article is more than two clicks from anywhere. */}
          <Link
            to="/about"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            about
          </Link>
          <Link
            to="/blog"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            blog
          </Link>
          <Link
            to="/bible"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            books of the bible
          </Link>
          <Link
            to="/block"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            block an app
          </Link>
          <Link
            to="/prayers"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            prayers
          </Link>
          <Link
            to="/verse"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            passages
          </Link>
          <Link
            to="/verses"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            verses
          </Link>
          <Link
            to="/quiz"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            quizzes
          </Link>
          <Link
            to="/privacy"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            privacy
          </Link>
          <Link
            to="/terms"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            terms
          </Link>
          <Link
            to="/support"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            support
          </Link>
          <Link
            to="/delete"
            className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            delete account
          </Link>
        </div>
      </footer>
    </div>
  );
}
